import Box from '@mui/material/Box';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { KeyboardEvent, FocusEvent, useState } from 'react';
import {
  AnyPromotionInfo,
  DiscountProductInfo,
  findPromotionByCampaignCode,
  Feature,
  getFeatureByAddOn,
  LineItem,
  OrderProductPreview,
  PlanInterval,
  RecurlyAddOn,
  Subscription,
} from '../../../../models';
import { fromPromotion } from '../../../../store';
import { formatCurrency } from '../../../../components';
import { TrackingPayload } from '../../../../utils/utils';
import { DEFAULT_CURRENCY_FORMAT_OPTIONS } from '../../../../utils/Constants';
import {
  getBundleComponentLineItems,
  getBundleFreeCrossBrowserLineItem,
  getPreviewExistingQuantity,
  getPaidAddOnLineItems,
  getPreviewProductFeature,
  getTrueBundleTitleId,
  isMonthlyToAnnualSwitchPreview,
  isTrueBundlePreviewProduct,
} from './trueBundlePreview';
import { getStarterPackageSummaryLine } from './starterPackageCampaign';
import { getSummaryQuantityLabel, getSummaryUnitWord } from './summaryUnits';
import { TRUE_BUNDLE_STARTER_FEATURES } from './trueBundlePlans';
import {
  formatTrueBundleComponentName,
  formatTrueBundleSummaryComponent,
  getTrueBundleFreeCrossBrowserSessions,
} from './trueBundleComponents';

const useStyles = makeStyles(theme => ({
  productRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(1.4, 0),
  },
  productBlock: {
    borderBottom: '1px solid #F0EFF6',
  },
  productName: {
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 1.35,
    color: '#16141d',
  },
  productQuantity: {
    fontSize: 12,
    lineHeight: 1.5,
    color: '#5b6661',
    marginTop: theme.spacing(0.25),
  },
  productAmount: {
    fontWeight: 800,
    fontSize: 15,
    lineHeight: 1.35,
    color: '#22283c',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  productOriginalAmount: {
    fontSize: 12.5,
    color: '#9CA3AF',
    textDecoration: 'line-through',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  productTaxAmount: {
    fontSize: 12.5,
    color: '#808B9A',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  promotionNote: {
    backgroundColor: '#ECFDF5',
    border: '1px solid #A7F3D0',
    borderRadius: 8,
    color: '#047857',
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.45,
    margin: theme.spacing(0.25, 0, 0.75),
    padding: theme.spacing(1, 1.25),
  },
  promotionNoteContent: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  promotionNoteInfoIcon: {
    color: '#047857',
    cursor: 'help',
    fontSize: 16,
    flexShrink: 0,
  },
  lightTooltip: {
    maxWidth: 360,
    padding: theme.spacing(1.25, 1.5),
    backgroundColor: '#ffffff',
    color: '#374151',
    borderRadius: 6,
    fontSize: 12,
    lineHeight: 1.45,
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
  },
  lightTooltipArrow: {
    color: '#ffffff',
  },
  billedPill: {
    display: 'inline-block',
    fontFamily: "'JetBrains Mono', ui-monospace, Menlo, monospace",
    fontWeight: 600,
    fontSize: 11,
    letterSpacing: '.06em',
    color: '#16141d',
    textTransform: 'uppercase',
    padding: theme.spacing(0.375, 1.125),
    borderRadius: 999,
    marginBottom: theme.spacing(1),
  },
  billedPillYear: {
    backgroundColor: '#dcf1e8',
  },
  billedPillMonth: {
    backgroundColor: '#ecebfb',
  },
  starterLine: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(0.75),
    fontSize: 12,
    lineHeight: 1.45,
    color: '#5b6661',
  },
  starterLineLabel: {
    minWidth: 0,
    display: 'inline-flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: theme.spacing(0.625),
  },
  starterLineTag: {
    fontSize: 12,
    fontWeight: 600,
    color: '#8a7218',
  },
  starterLineAmount: {
    whiteSpace: 'nowrap',
    color: '#3c3a50',
  },
  bundleComponentRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    paddingLeft: theme.spacing(1.5),
    fontSize: 12.5,
    lineHeight: 1.5,
    color: '#5b6661',
  },
  bundleComponentIncluded: {
    whiteSpace: 'nowrap',
    color: '#5b6661',
  },
  bundleComponentList: {
    paddingBottom: theme.spacing(1),
  },
  // Payment & Billing (design 6.10): the summary beside the payment form.
  productBlockCheckout: {
    borderBottom: '1px solid #eef2f0',
    '& $productRow': {
      padding: '10px 0',
    },
    '& $bundleComponentRow': {
      paddingLeft: 0,
      fontSize: 12,
      marginTop: theme.spacing(0.5),
    },
    '& $bundleComponentList': {
      paddingBottom: theme.spacing(1.25),
    },
  },
  // The last annual row before the monthly block gets the design's heavier divider.
  productBlockCycleEnd: {
    borderBottom: '2px solid #dedce9',
  },
  priceColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    flex: 'none',
  },
  quantityStepper: {
    display: 'inline-flex',
    alignItems: 'center',
    height: 26,
    marginTop: theme.spacing(0.75),
    border: '1px solid #cdd6d2',
    borderRadius: 8,
    backgroundColor: '#ffffff',
  },
  quantityStepperButton: {
    width: 28,
    height: 24,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    border: 'none',
    background: 'none',
    color: '#16141d',
    cursor: 'pointer',
    '&:first-child': {
      borderRadius: '8px 0 0 8px',
    },
    '&:last-child': {
      borderRadius: '0 8px 8px 0',
    },
    '&:hover': {
      backgroundColor: '#f2f7f4',
    },
    '&:disabled': {
      color: '#9aa5a0',
      cursor: 'default',
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
  },
  quantityStepperValue: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    minWidth: 72,
    height: 24,
    padding: '0 4px',
    boxSizing: 'border-box',
    fontSize: 12,
    lineHeight: '18px',
    color: '#16141d',
    whiteSpace: 'nowrap',
  },
  quantityStepperInput: {
    height: 20,
    padding: 0,
    margin: 0,
    border: 'none',
    textAlign: 'center',
    font: 'inherit',
    lineHeight: '20px',
    color: 'inherit',
    background: 'none',
    fontVariantNumeric: 'tabular-nums',
    outline: 'none',
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    MozAppearance: 'textfield',
  },
  removeButton: {
    marginTop: theme.spacing(1.25),
    marginRight: -8,
    width: 32,
    height: 32,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    border: 'none',
    borderRadius: 8,
    background: 'none',
    color: '#5b6661',
    cursor: 'pointer',
    // Issue #1274 #5: revealed on hover of its product row (and on keyboard focus); pointers
    // without hover (touch) keep it visible.
    opacity: 0,
    transition: 'opacity 120ms',
    '$productBlock:hover &, &:focus-visible': {
      opacity: 1,
    },
    '@media (hover: none)': {
      opacity: 1,
    },
    '&:hover': {
      color: '#b42f2f',
      backgroundColor: '#fdeeee',
    },
  },
  // Design 6.10 package lines: "1 seat at $1,000/yr [Starter package]" - the tag is a chip after
  // the label, a line's amount sits at the right in the body size.
  checkoutLine: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
    width: '100%',
    marginTop: theme.spacing(1),
    fontSize: 12,
    lineHeight: 1.5,
    color: '#5b6661',
  },
  checkoutLineTag: {
    display: 'inline-flex',
    alignItems: 'center',
    height: 20,
    padding: '0 8px',
    borderRadius: 999,
    backgroundColor: '#fef7d1',
    fontSize: 12,
    fontWeight: 600,
    color: '#6b5a12',
    whiteSpace: 'nowrap',
  },
  checkoutLineAmount: {
    flex: 'none',
    marginLeft: 'auto',
    whiteSpace: 'nowrap',
    fontSize: 14,
    color: '#16141d',
  },
}));

export { getSummaryUnitWord, getSummaryQuantityLabel } from './summaryUnits';

interface OrderSummaryLineItemsProps {
  selectedProducts: OrderProductPreview[];
  currentSubscriptions: Subscription[];
  groupByInterval?: boolean;
  onPromotionTooltipOpen?: (payload: TrackingPayload) => void;
  /**
   * `checkout` (design 6.10, Payment & Billing): package lines read "1 seat at $1,000/yr" with a
   * chip tag, no struck list price, bundle components without "included", and the products in
   * [editableFeatures] get an inline quantity stepper (+ a remove button unless [isRemovable]
   * says otherwise) that reports through [onQuantityChange] / [onRemove].
   */
  variant?: 'plan' | 'checkout';
  editableFeatures?: Feature[];
  isRemovable?: (feature: Feature) => boolean;
  onQuantityChange?: (feature: Feature, quantity: number) => void;
  onRemove?: (feature: Feature) => void;
}

/** Inline quantity stepper of a checkout row: -/+ apply at once, a typed value on blur/Enter. */
/** Hard cap of a self-serve line's quantity, shared with the add-products dialog (design: 500). */
export const ORDER_SUMMARY_MAX_QUANTITY = 500;

const QuantityStepper = ({
  feature,
  quantity,
  productTitle,
  onChange,
}: {
  feature: Feature;
  quantity: number;
  productTitle: string;
  onChange: (quantity: number) => void;
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const [draft, setDraft] = useState<string | undefined>(undefined);
  const unitWord = getSummaryUnitWord(feature, 1);
  const commitDraft = () => {
    if (draft === undefined) return;
    // A typed value above the cap is clamped, same as the dialog's stepper.
    const parsed = Math.min(Number.parseInt(draft, 10), ORDER_SUMMARY_MAX_QUANTITY);
    setDraft(undefined);
    if (parsed >= 1 && parsed !== quantity) {
      onChange(parsed);
    }
  };

  return (
    <span className={classes.quantityStepper} data-testid={`order-summary-stepper-${feature.toLowerCase()}`}>
      <button
        type="button"
        className={classes.quantityStepperButton}
        disabled={quantity <= 1}
        aria-label={intl.formatMessage(
          { id: 'page.plan.v2.order.summary.quantity.decrease.aria' },
          { unit: unitWord },
        )}
        onClick={() => onChange(quantity - 1)}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" aria-hidden="true">
          <path d="M6 12h12" />
        </svg>
      </button>
      <span className={classes.quantityStepperValue}>
        <input
          type="text"
          inputMode="numeric"
          className={classes.quantityStepperInput}
          style={{ width: `${String(draft ?? quantity).length + 1}ch` }}
          value={draft ?? String(quantity)}
          aria-label={intl.formatMessage(
            { id: 'page.plan.v2.order.summary.quantity.aria' },
            { product: productTitle },
          )}
          onChange={event => setDraft(event.target.value.replace(/\D/g, ''))}
          onBlur={(event: FocusEvent<HTMLInputElement>) => {
            event.preventDefault();
            commitDraft();
          }}
          onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') commitDraft();
          }}
        />
        <span>{getSummaryUnitWord(feature, Number(draft ?? quantity) || quantity)}</span>
      </span>
      <button
        type="button"
        className={classes.quantityStepperButton}
        disabled={quantity >= ORDER_SUMMARY_MAX_QUANTITY}
        aria-label={intl.formatMessage(
          { id: 'page.plan.v2.order.summary.quantity.increase.aria' },
          { unit: unitWord },
        )}
        onClick={() => onChange(quantity + 1)}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" aria-hidden="true">
          <path d="M6 12h12M12 6v12" />
        </svg>
      </button>
    </span>
  );
};

export const OrderSummaryLineItems = ({
  selectedProducts,
  currentSubscriptions,
  groupByInterval = false,
  onPromotionTooltipOpen,
  variant = 'plan',
  editableFeatures = [],
  isRemovable,
  onQuantityChange,
  onRemove,
}: OrderSummaryLineItemsProps) => {
  const classes = useStyles();
  const intl = useIntl();
  const isCheckout = variant === 'checkout';
  const activePromotion = useSelector(fromPromotion.selectActivePromotion);
  const activeCampaigns = useSelector(fromPromotion.selectActiveCampaigns);
  // Several campaigns can be active at once (one per Starter SKU), so a row's package lines must
  // be read from the campaign the PREVIEW applied, not from whichever campaign `/active` returned.
  const getAppliedCampaign = (
    appliedPromotion: DiscountProductInfo | undefined,
  ): AnyPromotionInfo | null => findPromotionByCampaignCode(
    activeCampaigns,
    activePromotion,
    appliedPromotion?.campaignCode,
  );

  const getProductInterval = (product: OrderProductPreview): PlanInterval => product.billingInterval
    ?? PlanInterval.getIntervalFromPlanCode(product.productCode);

  const suffixForInterval = (interval: PlanInterval) => intl.formatMessage({
    id: interval === PlanInterval.YEAR
      ? 'page.plan.v2.true.testing.price.year'
      : 'page.plan.v2.true.testing.price.month',
  });

  const formatPeriodPriceForInterval = (amount: number, interval: PlanInterval) => (
    `${formatCurrency(intl, amount, DEFAULT_CURRENCY_FORMAT_OPTIONS)}${suffixForInterval(interval)}`
  );

  const selectedProductRows = selectedProducts.flatMap(product => {
    const isSwitchToAnnual = isMonthlyToAnnualSwitchPreview(product, currentSubscriptions);
    const billingInterval = getProductInterval(product);
    const feature = getPreviewProductFeature(product);
    const quantity = isSwitchToAnnual ? product.quantity : product.newQuantity;
    // Starter-package rows swap the generic promotion note for the package breakdown
    // lines of the Billing 2.0 design.
    const isBundle = isTrueBundlePreviewProduct(product);
    // A bundle's add-on lines ARE the package - their amounts stay on the parent row, which is
    // the only place a bundle ever shows a price (Decision 2). Every other product bills its
    // add-ons as their own summary rows, so their amounts are taken out of the product row here.
    const paidAddOns = isBundle ? [] : getPaidAddOnLineItems([product]);
    const paidAddOnSubTotal = paidAddOns.reduce((sum, item) => sum + item.subtotal, 0);
    const paidAddOnDiscount = paidAddOns.reduce((sum, item) => sum + item.discountAmount, 0);
    const paidAddOnTax = paidAddOns.reduce((sum, item) => sum + item.tax, 0);
    const productSubTotal = Math.max(product.subTotal - paidAddOnSubTotal, 0);
    const productDiscountAmount = Math.max(product.discountAmount - paidAddOnDiscount, 0);
    const starterLine = getStarterPackageSummaryLine(
      getAppliedCampaign(product.appliedPromotion),
      product.appliedPromotion,
      quantity,
      productSubTotal,
    );

    const components = isBundle ? getBundleComponentLineItems(product) : ([] as LineItem[]);
    const componentPairs = components
      .map(component => [component.addOnCode, component.quantity] as [string, number]);
    const freeCrossBrowserLine = getBundleFreeCrossBrowserLineItem(product);
    const isStarterBundle = isBundle && TRUE_BUNDLE_STARTER_FEATURES.includes(feature);
    // Design 23.9 names a Starter in the summary without its "Starter" suffix ("True Automation");
    // the tag line underneath says it is the Starter package.
    const starterSummaryTitleId = `page.plan.v2.order.summary.bundle.${feature.toLowerCase()}.title`;

    return [{
      key: product.productCode,
      feature,
      billingInterval,
      titleId: isStarterBundle ? starterSummaryTitleId : getTrueBundleTitleId(feature),
      // The BE names a bundle per channel ("True Manual Starter" online, "True Manual" direct),
      // so its own name wins over the i18n title when the preview carries one - except for the
      // online Starters, whose summary title is the design's short name.
      displayName: isBundle && !isStarterBundle ? product.displayName : undefined,
      isBundle,
      isStarterBundle,
      components,
      // Free TestCloud Cross Browser earned by the bundle's seats, drawn after the real
      // components. The BE preview line is the source of truth; a preview without it (BE
      // deployed before that line existed) falls back to the FE estimate.
      freeCrossBrowserSessions: isBundle
        ? freeCrossBrowserLine?.quantity
          ?? getTrueBundleFreeCrossBrowserSessions(componentPairs)
        : 0,
      freeCrossBrowserDisplayName: freeCrossBrowserLine?.displayName,
      quantity,
      total: product.total,
      subTotal: productSubTotal,
      discountAmount: productDiscountAmount,
      tax: Math.max(product.tax - paidAddOnTax, 0),
      isSwitchToAnnual,
      existingQuantity: isSwitchToAnnual ? getPreviewExistingQuantity(product) : 0,
      newQuantity: product.newQuantity,
      starterLine,
      showPromotionNote: !starterLine
        && feature === Feature.TRUE_AUTOMATION
        && product.billingInterval === PlanInterval.YEAR
        && (product.discountAmount > 0
          || (product.appliedPromotion?.discountAmount ?? 0) > 0),
      promotionDiscountAmount: product.appliedPromotion?.discountAmount ?? product.discountAmount,
      promotionDiscountQuantity: product.appliedPromotion?.discountQuantity
        ?? product.discountQuantity,
    }];
  });

  type ProductRow = (typeof selectedProductRows)[number];

  // Quantity caption under the product name when the row has no stepper: the Starter package
  // label (+ seats) on the plan page, otherwise the plain seat / license count.
  const renderQuantityCaption = (product: ProductRow) => {
    if (isCheckout && product.isStarterBundle) return null;
    if (product.isStarterBundle) {
      return (
        <Typography className={classes.productQuantity}>
          <Box component="span" className={classes.starterLineLabel}>
            <Typography component="span" className={classes.starterLineTag}>
              <FormattedMessage id="page.plan.v2.order.summary.starter.tag" />
            </Typography>
          </Box>
        </Typography>
      );
    }
    return (
      <Typography className={classes.productQuantity}>
        {product.isSwitchToAnnual
          ? intl.formatMessage(
            { id: 'page.plan.v2.order.summary.seats.total.value' },
            { count: product.quantity },
          )
          : getSummaryQuantityLabel(product.feature, product.quantity)}
      </Typography>
    );
  };

  const renderRemoveButton = (product: ProductRow, productTitle: string) => (
    <button
      type="button"
      id={`order-summary-remove-${product.feature.toLowerCase()}`}
      className={classes.removeButton}
      aria-label={intl.formatMessage(
        { id: 'page.plan.v2.order.summary.remove.aria' },
        { product: productTitle },
      )}
      title={intl.formatMessage(
        { id: 'page.plan.v2.order.summary.remove.aria' },
        { product: productTitle },
      )}
      onClick={() => onRemove?.(product.feature)}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 7h16M9 7V4.5h6V7M6.5 7l1 12.5h9l1-12.5M10 11v5M14 11v5" />
      </svg>
    </button>
  );

  const renderPriceColumn = (
    product: ProductRow,
    productTitle: string,
    productAmountBeforeTax: number,
    removable: boolean,
  ) => {
    if (product.total === undefined) return null;
    const showOriginalAmount = !isCheckout
      && !product.starterLine
      && (product.subTotal ?? 0) > productAmountBeforeTax;
    return (
      <Box className={classes.priceColumn}>
        <Typography className={classes.productAmount}>
          {formatPeriodPriceForInterval(productAmountBeforeTax, product.billingInterval)}
        </Typography>
        {showOriginalAmount && (
        <Typography className={classes.productOriginalAmount}>
          {formatPeriodPriceForInterval(product.subTotal ?? 0, product.billingInterval)}
        </Typography>
        )}
        {(product.tax ?? 0) > 0 && (
        <Typography className={classes.productTaxAmount}>
          <FormattedMessage
            id="page.plan.v2.order.summary.product.tax"
            values={{
              amount: formatCurrency(
                intl,
                product.tax,
                DEFAULT_CURRENCY_FORMAT_OPTIONS,
              ),
            }}
          />
        </Typography>
        )}
        {removable && renderRemoveButton(product, productTitle)}
      </Box>
    );
  };

  // A self-serve row on the checkout page carries a stepper instead of a static quantity.
  const isRowEditable = (product: ProductRow) => isCheckout
    && !product.isBundle
    && !product.isSwitchToAnnual
    && !!onQuantityChange
    && editableFeatures.includes(product.feature);

  // Only the "Starter package" chip: a "3-seat bundle" count misled customers because the
  // bundle's components come in different quantities (issue #1274, #3).
  const renderCheckoutStarterLine = () => (
    <Box className={classes.checkoutLine} style={{ marginTop: 6 }}>
      <span className={classes.checkoutLineTag}>
        <FormattedMessage id="page.plan.v2.order.summary.starter.tag" />
      </span>
    </Box>
  );

  const renderSwitchToAnnualNote = (product: ProductRow) => (
    <Typography className={classes.productQuantity}>
      <FormattedMessage
        id={product.newQuantity > 0
          ? 'page.plan.v2.order.summary.includes.current.new.seats'
          : 'page.plan.v2.order.summary.includes.current.seats'}
        values={{
          currentCount: product.existingQuantity,
          newCount: product.newQuantity,
        }}
      />
    </Typography>
  );

  const renderProductRow = (
    product: ProductRow,
    isCycleEnd = false,
  ) => {
    const productAmountBeforeTax = Math.max(
      (product.subTotal ?? 0) - (product.discountAmount ?? 0),
      0,
    );
    const promotionDiscountQuantity = product.promotionDiscountQuantity
      || product.quantity;
    const productTitle = product.displayName ?? intl.formatMessage({ id: product.titleId });
    const editable = isRowEditable(product);
    const removable = editable && !!onRemove && (isRemovable?.(product.feature) ?? true);

    return (
      <Box
        key={product.key}
        id={`order-summary-row-${product.feature.toLowerCase()}`}
        className={clsx(
          classes.productBlock,
          isCheckout && classes.productBlockCheckout,
          isCheckout && isCycleEnd && classes.productBlockCycleEnd,
        )}
      >
        <Box className={classes.productRow}>
          <Box minWidth={0} flex={1}>
            <Typography className={classes.productName}>
              {product.displayName ?? <FormattedMessage id={product.titleId} />}
            </Typography>
            {editable && (
              <QuantityStepper
                feature={product.feature}
                quantity={product.quantity}
                productTitle={productTitle}
                onChange={quantity => onQuantityChange?.(product.feature, quantity)}
              />
            )}
            {isCheckout && product.isStarterBundle && renderCheckoutStarterLine()}
            {!editable && renderQuantityCaption(product)}
            {product.isSwitchToAnnual && renderSwitchToAnnualNote(product)}
          </Box>
          {renderPriceColumn(product, productTitle, productAmountBeforeTax, removable)}
        </Box>
        {/* The free line stands on its own: it shows even when the preview lists no component. */}
        {(product.components.length > 0 || product.freeCrossBrowserSessions > 0) && (
          <Box className={classes.bundleComponentList}>
            {product.components.map(component => (
              <Box
                key={component.addOnCode}
                className={classes.bundleComponentRow}
                data-testid="bundle-component-line"
              >
                <span>
                  {/* An unknown add-on code must read as its Recurly name, never as a raw key. */}
                  {product.isStarterBundle
                    ? formatTrueBundleSummaryComponent(
                      intl,
                      component.addOnCode,
                      component.quantity,
                      component.displayName,
                    )
                    : `${formatTrueBundleComponentName(
                      intl,
                      component.addOnCode,
                      component.displayName,
                    )} · ${getSummaryQuantityLabel(
                      getFeatureByAddOn(component.addOnCode),
                      component.quantity,
                    )}`}
                </span>
                {!isCheckout && (
                  <span className={classes.bundleComponentIncluded}>
                    <FormattedMessage id="page.plan.v2.order.summary.bundle.included" />
                  </span>
                )}
              </Box>
            ))}
            {product.freeCrossBrowserSessions > 0 && (
              <Box
                className={classes.bundleComponentRow}
                id={`order-summary-${product.key}-free-cross-browser`}
                data-testid="bundle-component-line"
              >
                <span>
                  {formatTrueBundleSummaryComponent(
                    intl,
                    RecurlyAddOn.TESTCLOUD_SESSION_CROSSBROWSER_FREE,
                    product.freeCrossBrowserSessions,
                    product.freeCrossBrowserDisplayName,
                  )}
                </span>
                {!isCheckout && (
                  <span className={classes.bundleComponentIncluded}>
                    <FormattedMessage id="page.plan.v2.order.summary.bundle.included" />
                  </span>
                )}
              </Box>
            )}
          </Box>
        )}
        {product.starterLine && isCheckout && (
          <Box pb={1.25}>
            <Box className={classes.checkoutLine}>
              <span>
                {product.starterLine.kind === 'tiered' ? (
                  <FormattedMessage
                    id={product.starterLine.additionalSeats > 0
                      ? 'page.plan.v2.order.summary.checkout.starter.first'
                      : 'page.plan.v2.order.summary.checkout.starter.at'}
                    values={{
                      count: product.starterLine.packageSeats,
                      unit: getSummaryUnitWord(product.feature, product.starterLine.packageSeats),
                      price: formatCurrency(
                        intl,
                        product.starterLine.packageUnitPrice,
                        DEFAULT_CURRENCY_FORMAT_OPTIONS,
                      ),
                      interval: suffixForInterval(product.billingInterval),
                    }}
                  />
                ) : (
                  <FormattedMessage
                    id={product.starterLine.additionalSeats > 0
                      ? 'page.plan.v2.order.summary.checkout.starter.seats'
                      : 'page.plan.v2.order.summary.checkout.starter.seats.included'}
                    values={{
                      count: product.starterLine.packageSeats,
                      unit: getSummaryUnitWord(product.feature, product.starterLine.packageSeats),
                    }}
                  />
                )}
              </span>
              <span className={classes.checkoutLineTag}>
                <FormattedMessage id="page.plan.v2.order.summary.starter.tag" />
              </span>
              {product.starterLine.additionalSeats > 0 && (
                <span className={classes.checkoutLineAmount}>
                  {formatCurrency(
                    intl,
                    product.starterLine.packagePrice,
                    DEFAULT_CURRENCY_FORMAT_OPTIONS,
                  )}
                </span>
              )}
            </Box>
            {product.starterLine.additionalSeats > 0 && (
              <Box className={classes.checkoutLine}>
                <span>
                  <FormattedMessage
                    id="page.plan.v2.order.summary.starter.additional"
                    values={{
                      count: product.starterLine.additionalSeats,
                      unit: getSummaryUnitWord(
                        product.feature,
                        product.starterLine.additionalSeats,
                      ),
                      price: formatCurrency(
                        intl,
                        product.starterLine.additionalUnitPrice,
                        DEFAULT_CURRENCY_FORMAT_OPTIONS,
                      ),
                      interval: suffixForInterval(product.billingInterval),
                    }}
                  />
                </span>
                <span className={classes.checkoutLineAmount}>
                  {formatCurrency(
                    intl,
                    product.starterLine.additionalPrice,
                    DEFAULT_CURRENCY_FORMAT_OPTIONS,
                  )}
                </span>
              </Box>
            )}
          </Box>
        )}
        {product.starterLine && !isCheckout && (
          <Box pb={1}>
            <Box className={classes.starterLine}>
              <Box component="span" className={classes.starterLineLabel}>
                <Typography component="span" className={classes.starterLineTag}>
                  <FormattedMessage id="page.plan.v2.order.summary.starter.tag" />
                </Typography>
                {/* Tiered offers name the discounted per-seat rate ("· 3 licenses at
                    $1,000/yr" / "· first 3 licenses"); one-time offers describe the
                    package ("· 5 seats included" / "· 5 seats"). */}
                {product.starterLine.kind === 'tiered' ? (
                  <FormattedMessage
                    id={product.starterLine.additionalSeats > 0
                      ? 'page.plan.v2.order.summary.starter.seats.first'
                      : 'page.plan.v2.order.summary.starter.seats.at'}
                    values={{
                      count: product.starterLine.packageSeats,
                      unit: getSummaryUnitWord(
                        product.feature,
                        product.starterLine.packageSeats,
                      ),
                      price: formatCurrency(
                        intl,
                        product.starterLine.packageUnitPrice,
                        DEFAULT_CURRENCY_FORMAT_OPTIONS,
                      ),
                      interval: suffixForInterval(product.billingInterval),
                    }}
                  />
                ) : (
                  <FormattedMessage
                    id={product.starterLine.additionalSeats > 0
                      ? 'page.plan.v2.order.summary.starter.seats'
                      : 'page.plan.v2.order.summary.starter.seats.included'}
                    values={{
                      count: product.starterLine.packageSeats,
                      unit: getSummaryUnitWord(
                        product.feature,
                        product.starterLine.packageSeats,
                      ),
                    }}
                  />
                )}
              </Box>
              {product.starterLine.additionalSeats > 0 && (
                <Typography component="span" className={classes.starterLineAmount}>
                  {formatCurrency(
                    intl,
                    product.starterLine.packagePrice,
                    DEFAULT_CURRENCY_FORMAT_OPTIONS,
                  )}
                </Typography>
              )}
            </Box>
            {product.starterLine.additionalSeats > 0 && (
              <Box className={classes.starterLine}>
                <Box component="span" className={classes.starterLineLabel}>
                  <FormattedMessage
                    id="page.plan.v2.order.summary.starter.additional"
                    values={{
                      count: product.starterLine.additionalSeats,
                      unit: getSummaryUnitWord(
                        product.feature,
                        product.starterLine.additionalSeats,
                      ),
                      price: formatCurrency(
                        intl,
                        product.starterLine.additionalUnitPrice,
                        DEFAULT_CURRENCY_FORMAT_OPTIONS,
                      ),
                      interval: suffixForInterval(product.billingInterval),
                    }}
                  />
                </Box>
                <Typography component="span" className={classes.starterLineAmount}>
                  {formatCurrency(
                    intl,
                    product.starterLine.additionalPrice,
                    DEFAULT_CURRENCY_FORMAT_OPTIONS,
                  )}
                </Typography>
              </Box>
            )}
          </Box>
        )}
        {product.showPromotionNote && (
          <Box className={classes.promotionNote}>
            <Box component="span" className={classes.promotionNoteContent}>
              <FormattedMessage
                id="page.plan.v2.order.summary.true.automation.promotion.note"
                values={{
                  amount: formatCurrency(
                    intl,
                    product.promotionDiscountAmount,
                    DEFAULT_CURRENCY_FORMAT_OPTIONS,
                  ),
                  count: promotionDiscountQuantity,
                }}
              />
              <Tooltip
                arrow
                placement="top"
                classes={{
                  tooltip: classes.lightTooltip,
                  arrow: classes.lightTooltipArrow,
                }}
                onOpen={() => onPromotionTooltipOpen?.({
                  field: 'order_summary',
                  object: 'true_automation_promotion_tooltip',
                  action: 'view_tooltip',
                  promotion_discount_amount: product.promotionDiscountAmount,
                  promotion_discount_quantity: promotionDiscountQuantity,
                })}
                title={(
                  <FormattedMessage
                    id="page.plan.v2.order.summary.true.automation.promotion.tooltip"
                    values={{
                      amount: formatCurrency(
                        intl,
                        product.promotionDiscountAmount,
                        DEFAULT_CURRENCY_FORMAT_OPTIONS,
                      ),
                      count: promotionDiscountQuantity,
                    }}
                  />
                )}
              >
                <InfoOutlinedIcon className={classes.promotionNoteInfoIcon} />
              </Tooltip>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box mt={groupByInterval ? 0 : 1.5}>
      {groupByInterval
        ? [PlanInterval.YEAR, PlanInterval.MONTH]
          .filter(interval => selectedProductRows.some(
            row => row.billingInterval === interval,
          ))
          .map((interval, intervalIndex) => (
            <Box key={interval} mt={intervalIndex === 0 ? 0 : 1.75}>
              <Typography
                className={`${classes.billedPill} ${interval === PlanInterval.YEAR
                  ? classes.billedPillYear
                  : classes.billedPillMonth}`}
              >
                <FormattedMessage
                  id={interval === PlanInterval.YEAR
                    ? 'page.plan.v2.order.summary.billed.annually'
                    : 'page.plan.v2.order.summary.billed.monthly'}
                />
              </Typography>
              {selectedProductRows
                .filter(row => row.billingInterval === interval)
                .map((row, rowIndex, rows) => renderProductRow(
                  row,
                  interval === PlanInterval.YEAR
                    && rowIndex === rows.length - 1
                    && selectedProductRows.some(
                      other => other.billingInterval === PlanInterval.MONTH,
                    ),
                ))}
            </Box>
          ))
        : selectedProductRows.map(row => renderProductRow(row))}
    </Box>
  );
};
